import { useState } from "react";
import { Badge, Button, Drawer, FormField, Input, Select, Tabs, useToast } from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { CAP, useTestConnection, useUpdateConfig } from "./api";
import type { Automate } from "./types";
import { deviceHealth, disciplineLabel, fmtDateTime, PROTOCOLE_LABEL } from "./lib";
import { Troubleshooting } from "./components/Troubleshooting";

/**
 * DeviceDetailDrawer — the per-analyzer console: status, a connection test (gated lis.automate.connect),
 * a reconfigure form (gated lis.adaptateur.configure), and inline troubleshooting. Keyboard-first
 * (focus-trapped Drawer), FR/AR-RTL via logical layout.
 */
export function DeviceDetailDrawer({
  automate,
  canConnect,
  onClose,
}: {
  automate: Automate;
  canConnect: boolean;
  onClose: () => void;
}) {
  const { t } = useT();
  const [tab, setTab] = useState("etat");
  const health = deviceHealth(automate);

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: token.s2, flexWrap: "wrap" }}>
          {automate.libelle}
          <Badge tone={health.tone}>{t(health.labelKey)}</Badge>
        </span>
      }
    >
      <Tabs
        aria-label={t("lis.dashboard.detail")}
        value={tab}
        onChange={setTab}
        items={[
          {
            id: "etat",
            label: t("lis.dashboard.col.sante"),
            content: <EtatTab automate={automate} canConnect={canConnect} />,
          },
          {
            id: "config",
            label: t("lis.config.save"),
            content: <ConfigTab automate={automate} />,
          },
          {
            id: "diagnostic",
            label: t("lis.trouble.title"),
            content: <Troubleshooting />,
          },
        ]}
      />
    </Drawer>
  );
}

// ── État + test connexion ────────────────────────────────────────────────────────
function EtatTab({ automate, canConnect }: { automate: Automate; canConnect: boolean }) {
  const { t } = useT();
  const toast = useToast();
  const test = useTestConnection();
  const result = test.data;

  const runTest = () => {
    test.mutate(automate.id, {
      onError: (err) => toast.error(err.message, t("lis.test.fail")),
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: `${token.s2} ${token.s4}`,
          margin: 0,
        }}
      >
        <Dt>{t("lis.dashboard.col.automate")}</Dt>
        <Dd mono>{automate.code}</Dd>
        <Dt>{t("lis.dashboard.col.discipline")}</Dt>
        <Dd>{disciplineLabel(automate.discipline_code)}</Dd>
        {automate.link_state && (
          <>
            <Dt>{t("lis.link.lastSeen")}</Dt>
            <Dd mono>{fmtDateTime(automate.link_state.last_seen_at)}</Dd>
            {automate.link_state.reason && (
              <>
                <Dt>{t("lis.link.reason")}</Dt>
                <Dd mono>{automate.link_state.reason}</Dd>
              </>
            )}
          </>
        )}
        {automate.config && (
          <>
            <Dt>{t("lis.config.protocole")}</Dt>
            <Dd>{PROTOCOLE_LABEL[automate.config.protocole]}</Dd>
            <Dt>{t("lis.config.transport")}</Dt>
            <Dd>{t(`lis.transport.${automate.config.transport}`)}</Dd>
            <Dt>{t("lis.config.sens")}</Dt>
            <Dd>{t(`lis.sens.${automate.config.sens}`)}</Dd>
            <Dt>{t("lis.config.mode")}</Dt>
            <Dd>{t(`lis.mode.${automate.config.mode_interaction}`)}</Dd>
            {automate.config.adresse_ip && (
              <>
                <Dt>{t("lis.config.ip")}</Dt>
                <Dd mono>
                  {automate.config.adresse_ip}
                  {automate.config.port ? `:${automate.config.port}` : ""}
                </Dd>
              </>
            )}
          </>
        )}
      </dl>

      {canConnect && (
        <Button variant="primary" onClick={runTest} loading={test.isPending} block>
          {test.isPending ? t("lis.test.running") : t("lis.action.test")}
        </Button>
      )}

      {result && (
        <div
          role="status"
          style={{
            padding: token.s3,
            borderRadius: token.rMd,
            border: `1px solid ${result.ok ? token.success : token.warn}`,
            background: result.ok ? token.tintSuccess : token.tintHigh,
            display: "flex",
            gap: token.s2,
            alignItems: "flex-start",
          }}
        >
          <span aria-hidden style={{ color: result.ok ? token.success : token.warn }}>
            {result.ok ? "✓" : "▲"}
          </span>
          <div>
            <strong style={{ display: "block" }}>
              {result.ok ? t("lis.test.ok") : t("lis.test.fail")}
            </strong>
            {!result.ok && (
              <span style={{ color: token.slate, fontSize: token.fsBody }}>
                {result.reason === "config_absente"
                  ? t("lis.test.reason.config_absente")
                  : (result.message ?? t("lis.test.reason.invalide"))}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Reconfiguration (gated lis.adaptateur.configure) ─────────────────────────────
function ConfigTab({ automate }: { automate: Automate }) {
  const { t } = useT();
  const toast = useToast();
  const canConfigure = useCan(CAP.configure);
  const update = useUpdateConfig();
  const cfg = automate.config;

  const [ip, setIp] = useState(cfg?.adresse_ip ?? "");
  const [port, setPort] = useState(cfg?.port != null ? String(cfg.port) : "");
  const [sens, setSens] = useState(cfg?.sens ?? "unidirectionnel");
  const [mode, setMode] = useState(cfg?.mode_interaction ?? "push");
  const [actif, setActif] = useState(cfg?.actif ?? true);

  if (!canConfigure) {
    return (
      <p style={{ color: token.slate }}>
        {t("state.forbidden.title")} ·{" "}
        <span className="nv-mono nv-ltr-island" style={{ color: token.steel }}>
          {CAP.configure}
        </span>
      </p>
    );
  }

  const save = () => {
    const portNum = port.trim() ? Number(port.trim()) : undefined;
    update.mutate(
      {
        automateId: automate.id,
        patch: {
          ...(ip.trim() ? { adresse_ip: ip.trim() } : {}),
          ...(portNum !== undefined && !Number.isNaN(portNum) ? { port: portNum } : {}),
          sens,
          mode_interaction: mode,
          actif,
        },
      },
      {
        onSuccess: () => toast.success(t("lis.config.saved")),
        onError: (err) => toast.error(err.message, t("lis.config.saveError")),
      },
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s3 }}>
      <FormField label={t("lis.config.ip")}>
        {(p) => (
          <Input {...p} mono inputMode="numeric" value={ip} onChange={(e) => setIp(e.target.value)} placeholder="192.168.1.50" />
        )}
      </FormField>
      <FormField label={t("lis.config.port")}>
        {(p) => (
          <Input {...p} mono inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value)} placeholder="9100" />
        )}
      </FormField>
      <FormField label={t("lis.config.sens")}>
        {(p) => (
          <Select
            {...p}
            value={sens}
            onChange={(e) => setSens(e.target.value as typeof sens)}
            options={(["unidirectionnel", "bidirectionnel"] as const).map((v) => ({
              value: v,
              label: t(`lis.sens.${v}`),
            }))}
          />
        )}
      </FormField>
      <FormField label={t("lis.config.mode")}>
        {(p) => (
          <Select
            {...p}
            value={mode}
            onChange={(e) => setMode(e.target.value as typeof mode)}
            options={(["host_query", "push", "download"] as const).map((v) => ({
              value: v,
              label: t(`lis.mode.${v}`),
            }))}
          />
        )}
      </FormField>
      <label style={{ display: "flex", alignItems: "center", gap: token.s2, minBlockSize: token.minTouch }}>
        <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />
        <span>{t("lis.config.actif")}</span>
      </label>
      <Button variant="primary" onClick={save} loading={update.isPending} block>
        {t("lis.config.save")}
      </Button>
    </div>
  );
}

function Dt({ children }: { children: React.ReactNode }) {
  return <dt style={{ color: token.slate }}>{children}</dt>;
}
function Dd({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <dd
      className={mono ? "nv-mono nv-ltr-island" : undefined}
      style={{ margin: 0, color: token.ink }}
    >
      {children}
    </dd>
  );
}
